/**
 * Pipeline module tabs: plain show/hide toggle, no accordion/height logic
 * needed (see AbxLexikon.js for that pattern). Nav buttons and panels are
 * matched by their position among siblings, not by an id/index attribute —
 * both lists come from the same "tabs" ContentCollection in the same order.
 *
 * Phase bars start at width:0 (see PipelineModule.scss) and only grow to
 * their real percentage once the whole module scrolls into view — one
 * IntersectionObserver per module (not per bar, see ScrollReveal.js for
 * that per-element pattern) so all bars in the module start together the
 * moment it's reached, rather than each bar waiting on its own scroll
 * position. Within a single card, each row still starts STAGGER seconds
 * after the previous one (set as a --pipeline-module-delay custom property,
 * read by both the fill and its checkmark badge in PipelineModule.scss) —
 * rows don't wait for the previous one to finish filling before starting.
 *
 * @return {void}
 */
export default function InitPipelineModule() {
    const STAGGER = 0.15;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    document.querySelectorAll('[data-pipeline-module]').forEach(function (module) {
        const tabs = Array.from(module.querySelectorAll('.pipeline-module__tab-nav-item'));
        const panels = Array.from(module.querySelectorAll('.pipeline-module__panel'));

        // one tab means there's nothing to switch between — hide the nav
        // bar on the public site, but editors still need it visible in the
        // backend to add more tabs/rename this one (see PipelineModule.scss)
        const tabNav = module.querySelector('.pipeline-module__tab-nav');
        tabNav?.classList.toggle('pipeline-module__tab-nav--single', tabs.length <= 1);

        if (!tabs.length || !panels.length) return;

        const setActive = (index) => {
            tabs.forEach((tab, tabIndex) => tab.classList.toggle('is-active', tabIndex === index));
            panels.forEach((panel, panelIndex) => panel.classList.toggle('is-active', panelIndex === index));
        };

        tabs.forEach(function (tab, index) {
            tab.addEventListener('click', function () {
                setActive(index);
            });
        });

        setActive(0);

        module.querySelectorAll('.pipeline-module__card').forEach(function (card) {
            Array.from(card.querySelectorAll('.pipeline-module__bar-row')).forEach(function (row, index) {
                row.style.setProperty('--pipeline-module-delay', (index * STAGGER) + 's');
            });
        });

        const fills = Array.from(module.querySelectorAll('.pipeline-module__bar-fill'));
        if (!fills.length) return;

        if (reduceMotion || !('IntersectionObserver' in window)) {
            fills.forEach((fill) => fill.classList.add('is-visible'));
            return;
        }

        const observer = new IntersectionObserver((entries, obs) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) return;
                fills.forEach((fill) => fill.classList.add('is-visible'));
                obs.unobserve(module);
            });
        }, { threshold: 0.15, rootMargin: '0px 0px -10% 0px' });

        observer.observe(module);
    });
}
